import { AlertTriangle } from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import { Stat } from "~/pages/manifold-nextgen/components/Stat";
import { SearchIndexStatus, SearchOrphanResponse } from "~/services/search";
import { Fetched } from "./constants";

const sum = <T,>(items: T[], of: (item: T) => number) =>
  items.reduce((total, item) => total + of(item), 0);

/** Sized to the figure's line box, so the tile does not resize. */
const PENDING = <Skeleton className="block h-9 w-16" />;

/** Stands in for a figure whose request failed; the tables below say why. */
const FAILED = <span className="text-muted-foreground">—</span>;

const figure = <T,>({ data, error }: Fetched<T>, render: (data: T) => string) =>
  data ? render(data) : error ? FAILED : PENDING;

const GRID_CLS = "grid grid-cols-1 gap-4 sm:grid-cols-3";

/**
 * The three numbers that say whether the index is healthy. No single figure
 * separates a complete-but-unsearchable index from a current-but-incomplete one,
 * and each tile waits only for its own data.
 */
export const IndexHealth = ({
  status,
  orphans,
}: {
  status: Fetched<SearchIndexStatus>;
  orphans: Fetched<SearchOrphanResponse>;
}) => {
  const types = status.data?.types ?? [];
  const entities = sum(types, (type) => type.entities);
  const chunks = sum(types, (type) => type.chunks);
  const searchable = sum(types, (type) => type.current_chunks);
  const stale = chunks - searchable;
  // Blocked types are left out, as in the table: their source listed nothing.
  const orphaned = sum(
    (orphans.data?.types ?? []).filter((type) => !type.blocked),
    (type) => type.orphans
  );

  return (
    <div className={GRID_CLS}>
      <Stat
        label="Indexed entities"
        value={figure(status, () => entities.toLocaleString())}
        detail={status.data ? `${chunks.toLocaleString()} rows in the index` : undefined}
      />

      {/* Only rows on the current model version reach the vector leg. */}
      <Stat
        label="Searchable rows"
        value={figure(status, () => searchable.toLocaleString())}
        unit={status.data ? ` of ${chunks.toLocaleString()}` : undefined}
        detail={
          status.data && stale > 0 ? (
            <span className="flex items-center gap-1 text-amber-600 dark:text-amber-500">
              <AlertTriangle className="size-3.5" />
              {stale.toLocaleString()} on a superseded model — re-index to replace
            </span>
          ) : (
            "On the model version search reads"
          )
        }
      />

      <Stat
        label="Orphaned"
        value={figure(orphans, () => orphaned.toLocaleString())}
        detail="Indexed entities their source no longer has"
      />
    </div>
  );
};
